import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { chargerUrl, generateQrPngForPdf } from './qr-generator';

/**
 * PDF QR-sheet generator for field stickers.
 *
 * Single-charger PDF: one page (A4 portrait, 595x842 pt) with the
 * QR code centered, identity + location label below. Bulk variant
 * iterates a charger list, one charger per page — same layout
 * each time so the operator can hand a multi-page printout to
 * whoever installs the stickers.
 *
 * Library: pdf-lib runs entirely in-browser, no native deps. The
 * EVI logo is rendered as DM Sans text rather than embedded SVG
 * to keep the bundle size down (logo PNG round-tripping through
 * pdf-lib's embedPng adds ~30kB per page; standard fonts are free).
 *
 * Layout per page (top to bottom):
 *   1. Header: "EV INITIATIVE" wordmark, 18pt bold, top-center
 *   2. QR code: 400pt square, centered horizontally
 *   3. Charger identity: 14pt monospace, centered
 *   4. Location name: 12pt regular, centered, gray
 *   5. Footer: "Scan to start charging" copy, 10pt, centered
 *
 * Each text block has its own y-coordinate; the QR is the
 * load-bearing element so positioning is anchored to its center.
 */

interface SinglePdfArgs {
  chargerIdentity: string;
  locationId: string;
  locationName: string;
}

interface BulkPdfArgs {
  chargers: Array<{ identity: string }>;
  locationId: string;
  locationName: string;
}

const PAGE_WIDTH = 595;   // A4 portrait
const PAGE_HEIGHT = 842;
const QR_SIZE = 400;      // points; with the 1024px source image
                          // pdf-lib downsamples cleanly at print
const QR_PIXEL_SIZE = 1024;

/**
 * Render one charger as a single page on the supplied PDFDocument.
 * Reused by both the single-charger and bulk variants so layout
 * stays consistent. Returns nothing; mutates the doc.
 */
async function renderChargerPage(
  doc: PDFDocument,
  args: { chargerIdentity: string; locationId: string; locationName: string },
  fonts: { sans: import('pdf-lib').PDFFont; sansBold: import('pdf-lib').PDFFont; mono: import('pdf-lib').PDFFont },
): Promise<void> {
  const url = chargerUrl(args.chargerIdentity, args.locationId);
  const qrBytes = await generateQrPngForPdf(url, QR_PIXEL_SIZE);
  const qrImage = await doc.embedPng(qrBytes);

  const page = doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);

  // ── Header: EV INITIATIVE wordmark ─────────────────────────────
  const headerText = 'EV INITIATIVE';
  const headerSize = 18;
  const headerWidth = fonts.sansBold.widthOfTextAtSize(headerText, headerSize);
  page.drawText(headerText, {
    x: (PAGE_WIDTH - headerWidth) / 2,
    y: PAGE_HEIGHT - 80,
    size: headerSize,
    font: fonts.sansBold,
    color: rgb(0.04, 0.04, 0.04),
  });

  // ── QR code: 400pt square, vertical center anchor ─────────────
  // QR sits roughly center-page; explicit y so identity/location/
  // footer can stack predictably below it.
  const qrX = (PAGE_WIDTH - QR_SIZE) / 2;
  const qrY = PAGE_HEIGHT - 130 - QR_SIZE; // 130pt from top to QR top
  page.drawImage(qrImage, {
    x: qrX,
    y: qrY,
    width: QR_SIZE,
    height: QR_SIZE,
  });

  // ── Charger identity: monospace, 14pt, centered ───────────────
  const identitySize = 14;
  const identityWidth = fonts.mono.widthOfTextAtSize(args.chargerIdentity, identitySize);
  page.drawText(args.chargerIdentity, {
    x: (PAGE_WIDTH - identityWidth) / 2,
    y: qrY - 36,
    size: identitySize,
    font: fonts.mono,
    color: rgb(0.04, 0.04, 0.04),
  });

  // ── Location name: regular, 12pt, gray ────────────────────────
  const locSize = 12;
  const locWidth = fonts.sans.widthOfTextAtSize(args.locationName, locSize);
  page.drawText(args.locationName, {
    x: (PAGE_WIDTH - locWidth) / 2,
    y: qrY - 58,
    size: locSize,
    font: fonts.sans,
    color: rgb(0.4, 0.4, 0.4),
  });

  // ── Footer: scan-to-start copy ────────────────────────────────
  const footerText = 'Scan to start charging';
  const footerSize = 10;
  const footerWidth = fonts.sans.widthOfTextAtSize(footerText, footerSize);
  page.drawText(footerText, {
    x: (PAGE_WIDTH - footerWidth) / 2,
    y: 60,
    size: footerSize,
    font: fonts.sans,
    color: rgb(0.4, 0.4, 0.4),
  });
}

export async function generateChargerQrPdf(args: SinglePdfArgs): Promise<Blob> {
  const doc = await PDFDocument.create();
  const sans = await doc.embedFont(StandardFonts.Helvetica);
  const sansBold = await doc.embedFont(StandardFonts.HelveticaBold);
  const mono = await doc.embedFont(StandardFonts.Courier);

  await renderChargerPage(doc, args, { sans, sansBold, mono });

  const bytes = await doc.save();
  // BlobPart cast — TS5 strict ArrayBufferLike vs ArrayBuffer split.
  // pdf-lib returns a Uint8Array; runtime behaviour unchanged.
  return new Blob([bytes as BlobPart], { type: 'application/pdf' });
}

export async function generateLocationQrBulkPdf(args: BulkPdfArgs): Promise<Blob> {
  const doc = await PDFDocument.create();
  const sans = await doc.embedFont(StandardFonts.Helvetica);
  const sansBold = await doc.embedFont(StandardFonts.HelveticaBold);
  const mono = await doc.embedFont(StandardFonts.Courier);

  // Sequential per-page render. Awaiting each in turn keeps memory
  // bounded for large operator portfolios — the qrcode + embedPng
  // calls each allocate transient buffers, and Promise.all'ing them
  // would peak at N×buffer size simultaneously.
  for (const charger of args.chargers) {
    await renderChargerPage(
      doc,
      {
        chargerIdentity: charger.identity,
        locationId: args.locationId,
        locationName: args.locationName,
      },
      { sans, sansBold, mono },
    );
  }

  const bytes = await doc.save();
  return new Blob([bytes as BlobPart], { type: 'application/pdf' });
}
